/**
 * Boot.
 *
 * THE CSS IMPORT ORDER BELOW IS LOAD-BEARING. organic.css must come first and portal.css second.
 * Flip them and the page's own `a{color:var(--color-accent-700)}` loses to the design system's
 * `a{color:var(--color-accent)}`, and the `body` ground rules reorder underneath everything.
 *
 * organic.css is a byte-identical copy of the vendored design system and is never edited — its
 * remote font @import is stripped in the Vite pipeline instead, and the faces it needed are
 * self-hosted at the top of portal.css. See vite.config.ts.
 *
 * `<App />` is the gate (loading / signed out / unreachable / in); the provider has to sit OUTSIDE
 * it, because the gate's very first decision is made from `useMe()`.
 */

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import './styles/organic.css'
import './styles/portal.css'

import { App } from './App'
import { ErrorBoundary } from './components/ErrorBoundary'
import { StaffApp } from './staff/StaffApp'
import { PortalProvider } from './state/portal'

const container = document.getElementById('root')
if (!container) {
  // index.html is ours; if this ever fires, the HTML entry was replaced or a bundler rewrote it.
  throw new Error('#root is missing from index.html — the app has nowhere to mount.')
}

/*
  Two applications, one bundle, chosen by path.

  The staff console is mounted INSTEAD of the client portal, not inside it — it has its own cookie,
  its own session table and its own API, and it must not be able to reach `PortalProvider` or be
  reached by it. Branching here rather than inside `<App>` means the customer-facing tree is
  bit-for-bit what it was before the console existed: a change made for staff cannot alter what a
  client sees, because on a client's path none of that code is ever constructed.

  The server falls back to index.html for unknown paths, so /staff serves this same bundle.
*/
const path = window.location.pathname
const isStaffConsole =
  path === '/staff' ||
  path.startsWith('/staff/') ||
  // The Super Administration Zone is the same application: it signs in differently and shows more,
  // but it is the staff tree, and `StaffApp` decides which of the two it is drawing.
  path === '/admin' ||
  path.startsWith('/admin/')

createRoot(container).render(
  <StrictMode>
    {isStaffConsole ? (
      /*
        The boundary is not optional. Without it a throw anywhere in the console's render tree
        unmounts the whole page and leaves a blank white screen with nothing in it — which is
        exactly how a date-revival bug was reported: "I click a dog and no record appears." An
        error that says something is a bug report; a blank page is a mystery.
      */
      <ErrorBoundary>
        <StaffApp />
      </ErrorBoundary>
    ) : (
      <PortalProvider>
        <App />
      </PortalProvider>
    )}
  </StrictMode>,
)
